import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Table, 
    Statistic, 
    Row, 
    Col, 
    DatePicker, 
    Button, 
    Space, 
    Alert, 
    Tag, 
    Typography,
    Spin,
    message
} from 'antd';
import { ReloadOutlined, CalendarOutlined } from '@ant-design/icons';
import { api } from '../api/services';
import { 
    IVendorAccountingEntry, 
    IVendorAccountBalance, 
    IVendorAccountSummary,
    getTransactionSourceLabel
} from '../interfaces/accounting';
import { formatCurrency } from '../utils/formatters';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface VendorAccountingTabProps {
    vendorId: number;
}

export const VendorAccountingTab: React.FC<VendorAccountingTabProps> = ({ vendorId }) => {
    const [accountingSummary, setAccountingSummary] = useState<IVendorAccountSummary | null>(null);
    const [accountingEntries, setAccountingEntries] = useState<IVendorAccountingEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().subtract(30, 'day'),
        dayjs()
    ]);

    const loadAccountingSummary = async () => {
        try {
            setLoading(true);
            const summary = await api.getVendorAccountSummary(vendorId);
            setAccountingSummary(summary);
        } catch (error) {
            console.error('Error loading vendor accounting summary:', error);
            message.error('Failed to load accounting summary');
        } finally {
            setLoading(false);
        }
    };

    const loadAccountingEntries = async (fromDate?: string, toDate?: string) => {
        try {
            setEntriesLoading(true);
            const entries = await api.getVendorAccountingEntries(vendorId, fromDate, toDate);
            setAccountingEntries(entries);
        } catch (error) {
            console.error('Error loading vendor accounting entries:', error);
            message.error('Failed to load accounting entries');
        } finally {
            setEntriesLoading(false);
        }
    };

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        setDateRange(dates || [null, null]);
    };

    const handleRefresh = () => {
        loadAccountingSummary();
        const fromDate = dateRange[0]?.format('YYYY-MM-DD');
        const toDate = dateRange[1]?.format('YYYY-MM-DD');
        loadAccountingEntries(fromDate, toDate);
    };

    const handleFilterEntries = () => {
        const fromDate = dateRange[0]?.format('YYYY-MM-DD');
        const toDate = dateRange[1]?.format('YYYY-MM-DD');
        loadAccountingEntries(fromDate, toDate);
    };

    useEffect(() => {
        loadAccountingSummary();
        const fromDate = dateRange[0]?.format('YYYY-MM-DD');
        const toDate = dateRange[1]?.format('YYYY-MM-DD');
        loadAccountingEntries(fromDate, toDate);
    }, [vendorId]);

    const entriesColumns = [
        {
            title: 'Date',
            dataIndex: 'transactionDate',
            key: 'transactionDate',
            render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
            sorter: (a: IVendorAccountingEntry, b: IVendorAccountingEntry) => 
                dayjs(a.transactionDate).unix() - dayjs(b.transactionDate).unix(),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Reference',
            dataIndex: 'reference',
            key: 'reference',
            render: (ref: string) => ref || '-',
        },
        {
            title: 'Debit',
            dataIndex: 'debitAmount',
            key: 'debitAmount',
            render: (amount: number) => amount > 0 ? formatCurrency(amount) : '-',
            align: 'right' as const,
        },
        {
            title: 'Credit',
            dataIndex: 'creditAmount',
            key: 'creditAmount',
            render: (amount: number) => amount > 0 ? formatCurrency(amount) : '-',
            align: 'right' as const,
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            render: (source: number) => (
                <Tag color="purple">{getTransactionSourceLabel(source)}</Tag>
            ),
        },
        {
            title: 'Journal #',
            dataIndex: 'journalNumber',
            key: 'journalNumber',
            render: (ref: string) => ref || '-',
        },
        {
            title: 'Status',
            dataIndex: 'journalStatus',
            key: 'journalStatus',
            render: (status: string) => {
                const color = status === 'Posted' ? 'green' : status === 'Draft' ? 'orange' : 'red';
                return <Tag color={color}>{status || 'Unknown'}</Tag>;
            },
        }
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div>
            {/* Account Summary Cards */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Current Balance (A/P)"
                            value={accountingSummary?.account?.currentBalance || 0}
                            formatter={(value) => formatCurrency(Number(value))}
                            valueStyle={{ 
                                color: (accountingSummary?.account?.currentBalance || 0) > 0 ? '#cf1322' : '#3f8600' 
                            }}
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            Amount owed to vendor
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Recent Transactions"
                            value={accountingSummary?.recentActivity?.transactionCount || 0}
                            suffix="transactions"
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            {accountingSummary?.recentActivity?.period}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Credits"
                            value={accountingSummary?.recentActivity?.totalCredits || 0}
                            formatter={(value) => formatCurrency(Number(value))}
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            {accountingSummary?.recentActivity?.period}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Net Change"
                            value={accountingSummary?.recentActivity?.netChange || 0}
                            formatter={(value) => formatCurrency(Number(value))}
                            valueStyle={{ 
                                color: (accountingSummary?.recentActivity?.netChange || 0) > 0 ? '#cf1322' : '#3f8600' 
                            }}
                        />
                        <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                            {accountingSummary?.recentActivity?.period}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Account Details */}
            <Card style={{ marginBottom: 24 }}>
                <Row>
                    <Col span={12}>
                        <Title level={5}>Account Information</Title>
                        <p><strong>Account Code:</strong> {accountingSummary?.account?.code}</p>
                        <p><strong>Account Name:</strong> {accountingSummary?.account?.name}</p>
                        <p><strong>Account Status:</strong> 
                            <Tag color={accountingSummary?.account?.exists ? 'green' : 'orange'} style={{ marginLeft: 8 }}>
                                {accountingSummary?.account?.exists ? 'Active' : 'Not Created'}
                            </Tag>
                        </p>
                    </Col>
                </Row>
            </Card>

            {/* Account not exists warning */}
            {!accountingSummary?.account?.exists && (
                <Alert
                    message="No Accounting Activity"
                    description="This vendor doesn't have any accounting transactions yet. The account will be created automatically when the first transaction occurs."
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            {/* Transaction History */}
            <Card
                title="Transaction History"
                extra={
                    <Space>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                        />
                        <Button onClick={handleFilterEntries} icon={<CalendarOutlined />}>
                            Filter
                        </Button>
                        <Button onClick={handleRefresh} icon={<ReloadOutlined />}>
                            Refresh
                        </Button>
                    </Space>
                }
            >
                <Table
                    dataSource={accountingEntries}
                    columns={entriesColumns}
                    rowKey="id"
                    loading={entriesLoading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} of ${total} transactions`,
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>
        </div>
    );
};